'use client';
import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Container,
  Divider,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Hide, Show } from '@chakra-ui/react';
import { useRadioGroup } from '@chakra-ui/react';
import cn from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import slugify from 'react-slugify';

import ColorPicker from '@/app/_components/color-picker/ColorPicker';
import CustomImage from '@/app/_components/custom-image/CustomImage';
import LinkCustom from '@/app/_components/link/LinkCustom';
import { PriceFormat } from '@/app/_components/price-format/price-format';
import { useFloatingCTA } from '@/hooks/floating-cta.hook';
import { getImageLink } from '@/services/link.service';
import type { ImageTransformationKeys } from '@/types/files/files.types';
import type {
  NewCarData,
  NewVehicleColor,
  SpecificationsPricesType,
} from '@/types/new-cars/new-cars.types';

import Brochure from '../brochure/Brochure';

import Styles from './ColorSelectionPage.module.scss';

type Props = {
  specVehicle: SpecificationsPricesType;
  vehicleDetail: NewCarData;
  selectedColorDetails: NewVehicleColor | null;
  basePath: '/build-price' | '/buy-online';
};

const imageKeys: ImageTransformationKeys = {
  desktopKey: 'image-815-375',
  mobileKey: 'image-320-150',
};

const getColorImage = (color: NewVehicleColor, mmCode: string): string => {
  const modelSpecificImage =
    color.cms_new_vehicles_colours_id.model_colour_images?.find(
      (img) => img?.model_colour_images_id?.mm_code === mmCode,
    )?.model_colour_images_id?.image_no_background_left_angel;

  const defaultImage =
    color.cms_new_vehicles_colours_id?.image_no_background_left_angle;

  return getImageLink(modelSpecificImage) ?? getImageLink(defaultImage) ?? '';
};

const ColorSelectionPage = ({
  specVehicle,
  vehicleDetail,
  selectedColorDetails,
  basePath = '/build-price',
}: Props) => {
  const [selectedColor, setSelectedColor] = useState<NewVehicleColor | null>(
    selectedColorDetails
      ? selectedColorDetails
      : vehicleDetail?.colour && vehicleDetail?.colour[0]
        ? vehicleDetail?.colour[0]
        : null,
  );
  const [additionalCost, setAdditionalCost] = useState<string | null>(
    selectedColorDetails
      ? selectedColorDetails.cms_new_vehicles_colours_id.additional_cost
      : vehicleDetail?.colour &&
          vehicleDetail?.colour[0] &&
          vehicleDetail?.colour[0]?.cms_new_vehicles_colours_id
        ? vehicleDetail?.colour[0]?.cms_new_vehicles_colours_id?.additional_cost
        : null,
  );

  const initialRenderRef = useRef(true);

  const { setFormItem } = useFloatingCTA();

  useEffect(() => {
    setFormItem({
      type: 'new-car',
      newCar: vehicleDetail,
      mmCode: specVehicle?.mm_code,
      source:
        basePath === '/build-price'
          ? vehicleDetail?.source_bp
          : vehicleDetail?.source_bo,
    });

    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      if (!selectedColorDetails && selectedColor) {
        updateUrlParam(
          'colorDetail',
          getUrlColorName(selectedColor.cms_new_vehicles_colours_id.color_name),
        );
      }
    }
    return () => {
      initialRenderRef.current = true;
    };
  }, [selectedColor, selectedColorDetails]);

  const handleChange = (value: string) => {
    const color = vehicleDetail.colour?.find(
      (c) => c.cms_new_vehicles_colours_id.color_name === value,
    );

    if (color) {
      updateColorState(color);
      updateUrlParam(
        'colorDetail',
        getUrlColorName(color.cms_new_vehicles_colours_id.color_name),
      );
    }
  };

  const updateUrlParam = (param: string, value: string) => {
    const newSearchParams = new URLSearchParams(window.location.search);

    newSearchParams.set(param, value);
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}?${newSearchParams.toString()}`,
    );
  };

  const updateColorState = (color: NewVehicleColor) => {
    setSelectedColor(color);
    setAdditionalCost(color.cms_new_vehicles_colours_id.additional_cost);
  };

  const { getRadioProps, getRootProps } = useRadioGroup({
    defaultValue: selectedColorDetails
      ? selectedColorDetails?.cms_new_vehicles_colours_id?.color_name
      : vehicleDetail.colour?.[0]?.cms_new_vehicles_colours_id.color_name || '',
    onChange: handleChange,
  });

  const getUrlColorName = (colorName: string) => {
    return slugify(colorName);
  };

  const isVariantSpes = vehicleDetail?.spec_variant_specs?.some(
    (variantSpec) => specVehicle.mm_code === variantSpec?.mm_code?.mmcode,
  );

  const selectedColorImage = useMemo(() => {
    if (!selectedColor) return '';
    return getColorImage(selectedColor, specVehicle.mm_code);
  }, [selectedColor, specVehicle.mm_code]);

  return (
    <Box
      paddingTop={{ base: '100px', lg: '134px' }}
      paddingBottom={'0px'}
      className={Styles.colorWrapper}
    >
      <Container
        maxWidth={{ base: '100%', md: '1328px' }}
        alignContent={'center'}
        position={'relative'}
      >
        <Grid
          templateColumns={{ base: 'repeat(2, 1fr)', lg: 'repeat(12, 1fr)' }}
          gap={{ base: '40px', xl: '80px' }}
        >
          <Show breakpoint="(max-width: 991px)">
            <GridItem colSpan={{ base: 2, lg: 8 }}>
              <Box
                className={Styles.headingElement}
                marginBottom={{ bas: '0', lg: '40px' }}
              >
                <Text>{vehicleDetail?.main_title}</Text>
                <Heading
                  as="h1"
                  className={Styles.titleLg}
                  marginBottom={{ base: 0, md: '8px' }}
                >
                  {specVehicle?.title}
                </Heading>
                <Flex gap={'16px'} alignItems={'center'}>
                  <LinkCustom
                    className="btnTextLink"
                    href={`${basePath}/${vehicleDetail?.slug}?mmcode=${specVehicle?.mm_code}`}
                  >
                    Change model
                  </LinkCustom>
                  {isVariantSpes && (
                    <LinkCustom
                      className="btnTextLink"
                      href={`/new-cars/${vehicleDetail?.slug}/specs/${specVehicle?.mm_code}`}
                    >
                      View Specs
                    </LinkCustom>
                  )}
                  {vehicleDetail?.brochure && (
                    <Brochure
                      model={vehicleDetail.main_title}
                      brochure={vehicleDetail.brochure}
                      variant="compact"
                    />
                  )}
                </Flex>
              </Box>
            </GridItem>
          </Show>

          <GridItem colSpan={{ base: 2, lg: 8 }}>
            {selectedColor ? (
              <CustomImage
                imageSrc={selectedColorImage}
                imageKeys={imageKeys}
                alt={
                  selectedColor?.cms_new_vehicles_colours_id?.image_alt_tag ??
                  vehicleDetail?.image_alt_tag ??
                  vehicleDetail?.main_title
                }
              />
            ) : null}
          </GridItem>
          <GridItem colSpan={{ base: 2, lg: 4 }}>
            <Hide breakpoint="(max-width: 991px)">
              <Box className={Styles.headingElement} marginBottom={'48px'}>
                <Text>{vehicleDetail?.main_title}</Text>
                <Heading
                  as="h1"
                  className={Styles.titleLg}
                  marginBottom={{ base: 0, md: '8px' }}
                >
                  {specVehicle?.title}
                </Heading>
                <Flex gap={'16px'} alignItems={'center'}>
                  <LinkCustom
                    className="btnTextLink"
                    href={`${basePath}/${vehicleDetail?.slug}?mmcode=${specVehicle?.mm_code}`}
                  >
                    Change model
                  </LinkCustom>

                  {isVariantSpes && (
                    <LinkCustom
                      className="btnTextLink"
                      href={`/new-cars/${vehicleDetail?.slug}/specs/${specVehicle?.mm_code}`}
                    >
                      View Specs
                    </LinkCustom>
                  )}
                  {vehicleDetail?.brochure && (
                    <Brochure 
                      model={vehicleDetail.main_title} 
                      brochure={vehicleDetail.brochure} 
                      variant="compact"
                    />
                  )}
                </Flex>
              </Box>
            </Hide>
            <Box className={Styles.headingElement} marginBottom={'24px'}>
              <Heading as="h2" className={Styles.titleMd}>
                Choose your exterior colour
              </Heading>
            </Box>
            <Box {...getRootProps()}>
              <Flex
                wrap={{ base: 'nowrap', md: 'wrap' }}
                gap={'8px'}
                paddingBottom={'12px'}
                marginBottom={'12px'}
                maxWidth={{ base: '100%', lg: '240px' }}
                overflowX={{ base: 'auto', md: 'visible' }}
              >
                {vehicleDetail?.colour?.map((color) => (
                  <ColorPicker
                    key={color.id}
                    colorName={color.cms_new_vehicles_colours_id.color_name}
                    colorPrimary={color.cms_new_vehicles_colours_id.hex}
                    colorSecondary={
                      color?.cms_new_vehicles_colours_id?.is_two_tone
                        ? color.cms_new_vehicles_colours_id.hex_2
                        : null
                    }
                    additionalCosts={
                      color.cms_new_vehicles_colours_id.additional_cost || null
                    }
                    {...getRadioProps({
                      value: color.cms_new_vehicles_colours_id.color_name,
                    })}
                  />
                ))}
              </Flex>
              {selectedColor && (
                <Text fontSize="sm">
                  <strong>Selected:</strong>{' '}
                  {selectedColor.cms_new_vehicles_colours_id.color_name}
                </Text>
              )}

              {additionalCost && (
                <Box className={Styles.headingElement} marginTop={'24px'}>
                  <Heading as="h3" className={Styles.titleSm}>
                    <PriceFormat prefix="+R" value={additionalCost} />
                  </Heading>
                  <Text fontSize="sm">Including VAT</Text>
                </Box>
              )}
            </Box>
          </GridItem>
        </Grid>
        <Box className={Styles.modelFooter}>
          <Flex
            direction={{ base: 'column', md: 'row' }}
            width={'100%'}
            gap={{ base: '10px', md: '24px' }}
            className={Styles.modelColorContent}
            justifyContent={'space-between'}
          >
            <HStack
              direction={['column', 'row']}
              alignItems={'start'}
              spacing={{ base: '10px', md: '24px' }}
              overflowX={{ base: 'auto', md: 'visible' }}
            >
              <Box
                minWidth={{ base: '100px', md: 'auto' }}
                width={'min-content'}
                flex={'0 0 auto'}
              >
                <Text className={Styles.labelText}>Starting at</Text>
                <Text className={cn(Styles.lableBigText, Styles.priceText)}>
                  <PriceFormat
                    prefix="R"
                    suffix="*"
                    value={specVehicle?.price}
                  />
                </Text>
                <Text className={Styles.labelSmallText}>Including VAT</Text>
              </Box>
              {additionalCost && (
                <>
                  <Divider
                    orientation="vertical"
                    borderColor={'gray.300'}
                    h={{ base: '73px', sm: '60px', md: '75px' }}
                  />
                  <Box
                    minWidth={{ base: '100px', md: 'auto' }}
                    width={'min-content'}
                    flex={'0 0 auto'}
                  >
                    <Text className={Styles.labelText}>Additional costs</Text>
                    <Text className={cn(Styles.lableBigText, Styles.priceText)}>
                      <PriceFormat prefix="R" value={additionalCost} />
                    </Text>
                    <Text className={Styles.labelSmallText}>Including VAT</Text>
                  </Box>
                </>
              )}
              {vehicleDetail?.price_per_month_from && (
                <>
                  <Divider
                    orientation="vertical"
                    borderColor={'gray.300'}
                    h={{ base: '73px', sm: '60px', md: '75px' }}
                  />
                  <Box
                    minWidth={{ base: '100px', md: 'auto' }}
                    width={'min-content'}
                    flex={'0 0 auto'}
                  >
                    <Text className={Styles.labelText}>Installment from</Text>
                    <Text className={cn(Styles.lableBigText, Styles.priceText)}>
                      <PriceFormat
                        prefix="R"
                        value={vehicleDetail?.price_per_month_from}
                      />
                    </Text>
                    <Text className={Styles.labelSmallText}>Combined</Text>
                  </Box>
                </>
              )}
              <Divider
                orientation="vertical"
                borderColor={'gray.300'}
                h={{ base: '73px', sm: '60px', md: '75px' }}
              />
              <Box
                minWidth={{ base: '100px', md: 'auto' }}
                width={'min-content'}
                flex={'0 0 auto'}
              >
                <Text className={Styles.labelText}>Distance Warranty</Text>
                <Text className={cn(Styles.lableBigText, Styles.warrantyText)}>
                  {vehicleDetail?.images_warranty}
                </Text>
              </Box>
            </HStack>

            <Stack className={Styles.ctaBtnContainer}>
              <LinkCustom
                extraClass={Styles.ctaBtn}
                href={`${basePath}/${vehicleDetail.slug}/${specVehicle?.mm_code}/${getUrlColorName(selectedColor?.cms_new_vehicles_colours_id.color_name || '')}`}
              >
                <Text className={Styles.mobileHide}>Financing</Text>
                <Text className={Styles.mobileShow}>Next</Text>
                <ChevronRightIcon />
              </LinkCustom>
            </Stack>
          </Flex>
        </Box>
      </Container>
    </Box>
  );
};

export default ColorSelectionPage;
